import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { ChevronsUpDown, Check, Search, X } from 'lucide-react';

/**
 * Mobile-friendly customer picker.
 * - Native <input> for search
 * - Native scrollable <div> with touch-action:pan-y so mouse + touch scrolling works
 * - No cmdk / Command component (those swallow scroll events inside Radix Popover)
 */
const CustomerPicker = ({
    customers = [],
    value,
    onChange,
    placeholder = 'Kunde wählen...',
    triggerClassName = '',
    showAvatar = true,
    testId = 'customer-picker',
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef(null);

    const selected = useMemo(
        () => customers.find((c) => c.id === value),
        [customers, value]
    );

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return customers;
        return customers.filter((c) => (c.name || '').toLowerCase().includes(term));
    }, [customers, search]);

    useEffect(() => {
        if (open) {
            setSearch('');
            // Focus input after popover animation
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={`w-full justify-between font-normal ${triggerClassName}`}
                    data-testid={testId}
                >
                    {selected ? (
                        <span className="flex items-center gap-2 truncate">
                            {showAvatar && (
                                <span className="w-4 h-4 rounded bg-primary/10 flex items-center justify-center text-[10px] shrink-0">
                                    {selected.name?.charAt(0)}
                                </span>
                            )}
                            <span className="truncate">{selected.name}</span>
                        </span>
                    ) : (
                        <span className="text-muted-foreground">{placeholder}</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[--radix-popover-trigger-width] p-0"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <div className="flex flex-col">
                    {/* Search Input */}
                    <div className="flex items-center border-b px-3 py-2 gap-2">
                        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Kunde suchen..."
                            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                            data-testid={`${testId}-search`}
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="Suche zurücksetzen"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Scrollable List - native scroll with touch support */}
                    <div
                        className="overflow-y-auto overscroll-contain"
                        style={{
                            maxHeight: '240px',
                            WebkitOverflowScrolling: 'touch',
                            touchAction: 'pan-y',
                        }}
                        data-testid={`${testId}-list`}
                    >
                        {filtered.length === 0 ? (
                            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                                Kein Kunde gefunden
                            </div>
                        ) : (
                            <ul className="py-1">
                                {filtered.map((c) => {
                                    const isSelected = value === c.id;
                                    return (
                                        <li key={c.id}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onChange(c.id);
                                                    setOpen(false);
                                                }}
                                                className={`w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-muted active:bg-muted/80 transition-colors ${
                                                    isSelected ? 'bg-muted/60' : ''
                                                }`}
                                                data-testid={`${testId}-option-${c.id}`}
                                            >
                                                <Check
                                                    className={`h-4 w-4 shrink-0 ${
                                                        isSelected ? 'opacity-100' : 'opacity-0'
                                                    }`}
                                                />
                                                {showAvatar && (
                                                    <span className="w-5 h-5 rounded bg-primary/10 flex items-center justify-center text-[10px] shrink-0">
                                                        {c.name?.charAt(0)}
                                                    </span>
                                                )}
                                                <span className="truncate">{c.name}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default CustomerPicker;
